import React, { useState } from 'react';
import './Pricing.scss';

export const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: 'Free',
      price: { monthly: 0, yearly: 0 },
      description: 'Perfect for getting started',
      features: [
        'Join unlimited communities',
        'Access free courses',
        'Basic messaging',
        'Community forums',
        'Mobile app access'
      ],
      cta: 'Get Started',
      popular: false,
      color: '#6b7280'
    },
    {
      name: 'Pro',
      price: { monthly: 19, yearly: 190 },
      description: 'Best for active learners',
      features: [
        'Everything in Free',
        'Unlimited course access',
        'Priority support',
        '1-on-1 tutoring sessions',
        'Advanced analytics',
        'Certificate of completion',
        'Ad-free experience',
        'Download course materials'
      ],
      cta: 'Start Free Trial',
      popular: true,
      color: '#6366f1'
    },
    {
      name: 'Teacher',
      price: { monthly: 49, yearly: 490 },
      description: 'For professional educators',
      features: [
        'Everything in Pro',
        'Create unlimited courses',
        'Host live classes',
        'Create paid communities',
        'Advanced student analytics',
        'Custom branding',
        'Priority course listing',
        'Dedicated account manager',
        '80% revenue share'
      ],
      cta: 'Start Teaching',
      popular: false,
      color: '#8b5cf6'
    }
  ];

  const faqs = [
    {
      question: 'Can I switch plans anytime?',
      answer: 'Yes, you can upgrade, downgrade, or cancel your plan at any time. Changes take effect immediately.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes! Pro and Teacher plans come with a 14-day free trial. No credit card required.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, and bank transfers for annual subscriptions.'
    },
    {
      question: 'Can I get a refund?',
      answer: 'Yes, we offer a 30-day money-back guarantee for all paid plans.'
    },
    {
      question: 'Do you offer discounts for students?',
      answer: 'Yes! Students get 50% off all paid plans with a valid student ID.'
    },
    {
      question: 'What happens to my content if I cancel?',
      answer: 'Your content remains accessible for 30 days after cancellation. You can export it anytime.'
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <section className="hero-section text-center py-5">
        <div className="container">
          <h1 className="display-4 fw-bold mb-4" data-aos="fade-up">
            Simple, <span className="text-theme1">Transparent Pricing</span>
          </h1>
          <p className="lead text-muted mb-5 mx-auto" style={{ maxWidth: '700px' }} data-aos="fade-up" data-aos-delay="100">
            Choose the perfect plan for your learning or teaching journey. All plans include 14-day free trial.
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle d-flex justify-content-center align-items-center gap-3 mb-5" data-aos="fade-up" data-aos-delay="200">
            <span className={billingCycle === 'monthly' ? 'fw-bold' : 'text-muted'}>Monthly</span>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={billingCycle === 'yearly'}
                onChange={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                style={{ width: '3rem', height: '1.5rem' }}
              />
            </div>
            <span className={billingCycle === 'yearly' ? 'fw-bold' : 'text-muted'}>
              Yearly <span className="badge bg-success ms-2">Save 20%</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards py-5">
        <div className="container">
          <div className="row g-4 justify-content-center">
            {plans.map((plan, index) => (
              <div className="col-12 col-md-6 col-lg-4" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <div className={`pricing-card card h-100 border-0 shadow ${plan.popular ? 'popular' : ''}`}>
                  {plan.popular && (
                    <div className="popular-badge">
                      <span className="badge bg-theme1">Most Popular</span>
                    </div>
                  )}
                  <div className="card-body p-4">
                    <h3 className="card-title fw-bold mb-2">{plan.name}</h3>
                    <p className="text-muted mb-4">{plan.description}</p>
                    <div className="price-section mb-4">
                      <h2 className="price fw-bold mb-0">
                        ${plan.price[billingCycle]}
                        <span className="fs-6 text-muted fw-normal">
                          /{billingCycle === 'monthly' ? 'month' : 'year'}
                        </span>
                      </h2>
                      {billingCycle === 'yearly' && plan.price.yearly > 0 && (
                        <p className="text-muted small mb-0">
                          ${(plan.price.yearly / 12).toFixed(2)}/month billed annually
                        </p>
                      )}
                    </div>
                    <button 
                      className={`btn w-100 mb-4 ${plan.popular ? 'btn-theme1' : 'btn-outline-theme1'} btn-lg rounded-pill`}
                      style={{ borderColor: plan.color, ...(plan.popular ? { backgroundColor: plan.color } : { color: plan.color }) }}
                    >
                      {plan.cta}
                    </button>
                    <ul className="features-list list-unstyled">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="mb-3">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="comparison-section py-5 bg-light">
        <div className="container">
          <h2 className="text-center fw-bold mb-5" data-aos="fade-up">Feature Comparison</h2>
          <div className="table-responsive" data-aos="fade-up" data-aos-delay="100">
            <table className="table table-hover bg-white rounded shadow-sm">
              <thead className="table-light">
                <tr>
                  <th scope="col" className="border-0">Feature</th>
                  <th scope="col" className="text-center border-0">Free</th>
                  <th scope="col" className="text-center border-0">Pro</th>
                  <th scope="col" className="text-center border-0">Teacher</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Communities</td>
                  <td className="text-center"><i className="bi bi-check-circle-fill text-success"></i></td>
                  <td className="text-center"><i className="bi bi-check-circle-fill text-success"></i></td>
                  <td className="text-center"><i className="bi bi-check-circle-fill text-success"></i></td>
                </tr>
                <tr>
                  <td>Course Access</td>
                  <td className="text-center">Free only</td>
                  <td className="text-center">Unlimited</td>
                  <td className="text-center">Unlimited</td>
                </tr>
                <tr>
                  <td>Create Courses</td>
                  <td className="text-center"><i className="bi bi-x-circle-fill text-danger"></i></td>
                  <td className="text-center"><i className="bi bi-x-circle-fill text-danger"></i></td>
                  <td className="text-center">Unlimited</td>
                </tr>
                <tr>
                  <td>Live Classes</td>
                  <td className="text-center"><i className="bi bi-x-circle-fill text-danger"></i></td>
                  <td className="text-center">Join only</td>
                  <td className="text-center">Host unlimited</td>
                </tr>
                <tr>
                  <td>Support</td>
                  <td className="text-center">Email</td>
                  <td className="text-center">Priority</td>
                  <td className="text-center">Dedicated</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <div className="container">
          <h2 className="text-center fw-bold mb-5" data-aos="fade-up">Frequently Asked Questions</h2>
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="accordion" id="faqAccordion">
                {faqs.map((faq, index) => (
                  <div className="accordion-item border-0 shadow-sm mb-3" key={index} data-aos="fade-up" data-aos-delay={index * 50}>
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openFaq === index ? '' : 'collapsed'}`}
                        type="button"
                        onClick={() => toggleFaq(index)}
                      >
                        {faq.question}
                      </button>
                    </h2>
                    <div className={`accordion-collapse collapse ${openFaq === index ? 'show' : ''}`}>
                      <div className="accordion-body">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
